import { helpers } from '@cargo/common';
import _ from 'lodash';

let counter = 0;
const isMac = helpers.isMac();

class HotKeyManager {
    constructor() {
        if (helpers.isServer) { return }

        if (!HotKeyManager.instance) {
            this.shortcuts = {};     // Stores shortcuts per scope
            this.scopeOrder = [];    // Pre-defined scope priority order
            this.onKeyDown = this.onKeyDown.bind(this);

            if ( typeof window !== 'undefined' ) {
                window.addEventListener('keydown', this.onKeyDown);
                // window.HotKeyManager = this;
            }

            HotKeyManager.instance = this;
        }

        return HotKeyManager.instance;
    }

    setScopeOrder(scopes) {
        if (this.scopeOrder && _.isEmpty(this.scopeOrder)) {
            _.each(scopes, (scope) => { this.scopeOrder.push(scope); });
        }
    }

    getConfigKey(config) {
        const { keyCode, metaKey = "false", shiftKey = "false", altKey = "false", macCtrlKey = "false" } = config;
        return `${keyCode}-${metaKey}-${shiftKey}-${altKey}-${macCtrlKey}`;
    }

    getEventConfig(event) {
        return {
            keyCode: event.keyCode,
            metaKey: isMac ? event.metaKey : event.ctrlKey,
            shiftKey: event.shiftKey,
            altKey: event.altKey,
            macCtrlKey: isMac ? event.ctrlKey : false,
        };
    }

    registerHotKey(shortcutName, config, callback, scopeName = 'default') {
        const id = counter++;

        const configKey = this.getConfigKey(config);

        // Initialize the scope in the shortcuts if it doesn't exist
        if (!this.shortcuts[scopeName]) {
            this.shortcuts[scopeName] = {};
        }

        // Initialize the stack for the configKey in the scope if it doesn't exist
        if (!this.shortcuts[scopeName][configKey]) {
            this.shortcuts[scopeName][configKey] = [];
        }

        // Push the shortcut onto the stack
        this.shortcuts[scopeName][configKey].push({ id, callback });

        // Return unregister function
        return () => {
            const stack = this.shortcuts[scopeName][configKey];
            if (stack) {
                // Find the index of the shortcut with this id
                const index = stack.findIndex(item => item.id === id);
                if (index !== -1) {
                    stack.splice(index, 1);
                    // If the stack is empty, delete the key
                    if (stack.length === 0) {
                        delete this.shortcuts[scopeName][configKey];
                        // If the scope has no shortcuts, delete the scope
                        if (Object.keys(this.shortcuts[scopeName]).length === 0) {
                            delete this.shortcuts[scopeName];
                        }
                    }
                }
            }
        };
    }

    onKeyDown(event) {
        if (event.repeat) return;

        const eventConfig = this.getEventConfig(event);
        const configKey = this.getConfigKey(eventConfig);

        // Iterate over the scopes based on the pre-defined priority order
        for (let i = 0; i < this.scopeOrder.length; i++) {
            const scopeName = this.scopeOrder[i];
            const scopeShortcuts = this.shortcuts[scopeName];
            if (scopeShortcuts && scopeShortcuts[configKey]) {
                const stack = scopeShortcuts[configKey];
                if (stack && stack.length > 0) {
                    // Get and execute the most recently registered shortcut
                    const shortcut = stack[stack.length - 1];
                    if (shortcut && shortcut.callback) {
                        event.preventDefault();
                        shortcut.callback(event);
                        return; // Stop after executing the first matching shortcut
                    }
                }
            }
        }

        // If no scope handled the shortcut, check the 'default' scope
        if (!this.scopeOrder.includes('default') && this.shortcuts['default'] && this.shortcuts['default'][configKey]) {
            const stack = this.shortcuts['default'][configKey];
            if (stack && stack.length > 0) {
                // Get and execute the most recently registered shortcut
                const shortcut = stack[stack.length - 1];
                if (shortcut && shortcut.callback) {
                    event.preventDefault();
                    shortcut.callback(event);
                }
            }
        }
    }
}

// Create a singleton instance
const hotKeyManager = new HotKeyManager();
Object.freeze(hotKeyManager);

// Export the singleton instance as a named export
export { hotKeyManager as HotKeyManager };

// Use in a the JSX of a class based component like this:

// <HotKey 
//    hotkey="cmd+shift+g"
//    hotKeyConfig={{ keyCode: 71, metaKey: true,	shiftKey: true, altKey: false, macCtrlKey: false}}
//    callBackFunction={this.toggleDevGridMode}
//    scope="default"
//  />

// Use in a functional component like this:

// useHotKey(
//     'escape',
//     { keyCode: 27 },
//     callback,
//     'modal' 
// );

// Can also bind in a class based component like this:

// unregisterHotKeys = [];

// componentDidMount() {
//   const unregisterEscape = HotKeyManager.registerHotKey(
//     'escape',
//     { keyCode: 27 },
//     this.handleEscapeKey,
//     'alert'
//   );

//   const unregisterDelete = HotKeyManager.registerHotKey(
//     'delete',
//     { keyCode: 8 },
//     this.handleDeleteKey
//     'contextmenu'
//   );

//   this.unregisterHotKeys.push(unregisterEscape, unregisterDelete);
// }

// componentWillUnmount() {
//   this.unregisterHotKeys.forEach((unregister) => unregister());
// }
